import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for managing Google Maps instances
 * Provides utilities for map initialization, marker management, and cleanup
 */
export const useGoogleMaps = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  // Check if Google Maps API is already loaded
  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsLoaded(true);
    }
  }, []);

  /**
   * Load Google Maps API if not already loaded
   */
  const loadGoogleMaps = useCallback(async () => {
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return window.google;
    }

    try {
      setError(null);

      // Get API key from environment variable (this is the correct approach for Google Maps JS API)
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        throw new Error('Google Maps API key not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY environment variable.');
      }

      // Check if script is already loading
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');

      if (!existingScript) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
        script.async = true;
        script.defer = true;

        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Google Maps API'));
          document.head.appendChild(script);
        });
      } else {
        // Wait for existing script to load
        await new Promise((resolve, reject) => {
          if (window.google) {
            resolve();
          } else {
            existingScript.onload = resolve;
            existingScript.onerror = () => reject(new Error('Failed to load Google Maps API'));
          }
        });
      }

      setIsLoaded(true);
      return window.google;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Create a new map instance
   */
  const createMap = useCallback((container, options) => {
    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps API not loaded');
    }

    const map = new window.google.maps.Map(container, options);
    mapInstanceRef.current = map;
    return map;
  }, []);

  /**
   * Add a marker to the map
   */
  const addMarker = useCallback((position, options = {}) => {
    if (!mapInstanceRef.current || !window.google) {
      return null;
    }

    const marker = new window.google.maps.Marker({
      position,
      map: mapInstanceRef.current,
      ...options
    });

    markersRef.current.push(marker);
    return marker;
  }, []);

  /**
   * Clear all markers from the map
   */
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    markersRef.current = [];
  }, []);

  /**
   * Update map center
   */
  const setCenter = useCallback((center) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setCenter(center);
    }
  }, []);

  /**
   * Update map zoom
   */
  const setZoom = useCallback((zoom) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(zoom);
    }
  }, []);

  /**
   * Cleanup function
   */
  const cleanup = useCallback(() => {
    clearMarkers();
    mapInstanceRef.current = null;
  }, [clearMarkers]);

  return {
    isLoaded,
    error,
    loadGoogleMaps,
    createMap,
    addMarker,
    clearMarkers,
    setCenter,
    setZoom,
    cleanup,
    mapInstance: mapInstanceRef.current
  };
};

export default useGoogleMaps;